import { useEffect, useRef, useState } from 'react';

/* Some Charts are special and come pre-loaded
  For now, let's just "know" these chart ids
  Maybe they can be loaded in via django seed data?
  TODO: ^^^ seed data?
  Would be good to plan for these to be configurable per-tenant
*/
export let known_chart_ids = {
    person_type: 'persontype',
    locations: 'locations',
    zipcodes: 'zip',
    counties: 'county_fips',
    congressional_districts: 'congress',
    states: 'state',
    cbsa: 'cbsa',
};

export function buildCharts(node, companyCode) {
   
    let selects = []
    if (companyCode) {
        selects.push({
            'id': 'locations',
            'uiType': 'locations',
            'label': 'AdHoc Locations',
            'ds': `${companyCode}_app`,
            'tags': {
                'filterable': 'true'
            }
        })
    }
    if (node && node.children) {
        node.children.forEach(child => {

            if(!child) {
                return;
            }

            if (child.group) {
                selects = selects.concat(buildCharts(child.group))
            }
            else if (!child.id) {
                console.log(`Error: ${JSON.stringify(child)}`)
            }
            else {
                child.value_labels = {}
                if (child.gui) {
                    child.color = child.gui.color
                    child.uiType = child.gui.uiType
                    if (child.tags && child.tags.gender) {
                        child.uiType = 'gender'
                    }
                    if (['dob', 'deceaseddate', 'equityloandate', 'mortgagedate', 'homepurdate'].includes(child.id)) {
                        child.uiType = 'entered-value'
                    }
                }
                if (child.vmap && child.vmap.value_labels) {
                    child.vmap.value_labels.forEach(([key, val]) => child.value_labels[key] = val)
                }
                if (child.plist) {
                    child.plist.forEach((v) => child.value_labels[v.value] = v.label)
                }
                selects.push(child)
            }
        });
    }
    return selects
}

export function buildTaxonomy(node) {
    var selects = []
    var nodes = {}
    if (node && node.children) {
        node.children.forEach(child => {                   
            if (child.group) {
                nodes[child.group.label] = buildTaxonomy(child.group)
            } 
            else if (!child.id) {
                console.log(`Error: ${JSON.stringify(child)}`)
            }
            else if ((child.id.indexOf('H3_') === -1) &&
                (child.tags && child.tags.filterable === "true")) {
                selects.push(child.id)
            }
        });
    }
    return [selects, nodes]
}

export default function useChartsStore(appState, query, explorerModule) {
    
    const [loaded, setLoaded] = useState(false);
    const [charts, setChartsState] = useState(null);
    const chartsRef = useRef(null);
    function setCharts(c) { chartsRef.current = c; setChartsState(c); }
    const [taxonomy, setTaxonomy] = useState([[], {}]);
    const [filterable] = useState([]);
    const [filterable2] = useState([]);
    const [orderable, setOrderable] = useState([]);
    const [sortable, setSortable] = useState([]);
    const [modelable, setModelable] = useState([]);
    const [life_events, setLifeEvents] = useState([]);
    const [h3FeatureMap, setH3FeatureMap] = useState({});
    const [comparable, setComparable] = useState([]);
    const [uploadsExtraData, setUploadsExtraData] = useState({});

    function findPathToFirstLeafNode(node) {
        if (node && node[0].length) return [];
        const firstKey = Object.keys(node[1])[0];
        if (!firstKey) return [];
        return [firstKey].concat(findPathToFirstLeafNode(node[1][firstKey]));
    }

    function pathIsInTaxonomy(path, node) {
        if (!path) return false;
        if (node && node[0].length) return path.length === 0;
        const key = path[0];
        if (node[1][key]) {
            return pathIsInTaxonomy(path.slice(1), node[1][key])
        }
        return false;
    }

    function loadChartsData() {
        
        const xhrConfig = { base_url: `${window.crossfilter_url}`};

        let _population = localStorage.getItem("am:explorer_module") || "consumers";

        if(!appState.activeCompany) { return; }
        setLoaded(false);

        query(`fetch/gui_group/${appState.activeCompany.code}/root_${_population}`, 
            { }, xhrConfig).then((response) => {
            
            const charts = {};
            buildCharts(response, appState.activeCompany.code).forEach((chart) => charts[chart.id] = chart);
            
            setOrderable(Object.values(charts)
                .filter(c => c.tags && c.tags.orderable === 'true')
                .map(c => {return {id: c.id, ds: c.ds, label: c.label, channel: c.tags.channel || ""}}));

            setSortable(Object.values(charts)
                .filter(c => c.tags && c.tags.sortable)
                .map(c => {return {id: c.id, ds: c.ds, label: c.label}}));

            setModelable(Object.values(charts)
                .filter(c => c.tags && c.tags.model === 'true')
                .map(c => {return {id: c.id, ds: c.ds, label: c.label}}));

            setLifeEvents(Object.values(charts)
              .filter(c => c.tags && c.tags.hasOwnProperty('life_event') && c.tags.life_event === 'true')
              .map(c => {return c.id}));

            setComparable(Object.values(charts)
                .filter(c => c.tags && c.tags.orderable === 'true')
                .filter(c => c.restrict_gather === false)
                .map(c => {return {id: c.id, ds: c.ds, label: c.label}}));

            // setOrderable(Object.values(charts).filter(c => c['tags'] && c['tags'].orderable === 'true'));
            // setSortable(Object.values(charts).filter(c =>  c['tags'] && c['tags'].sortable));
            // setH3Ds(Object.values(charts)
            //     .filter(c => c.tags && c.tags.h3)
            //     .map(c => c.ds)[0]);

            setH3FeatureMap(
                Object.values(charts)
                    .filter(c => c.tags && c.tags.h3)
                    .reduce(function(map, obj) {
                        map[obj.id] = {
                            id: obj.id,
                            ds: obj.ds,
                        };
                        return map;
                    }, {})
            );

            /* uploads have extra data in the label: split it into parts */
            const allUpdated = {};
            Object.keys(charts).forEach((val) => {
                if (val?.startsWith("up_")) {
                    const updated = {};
                    const simplified = {};

                    Object.entries(charts[val].value_labels).forEach(([key, value]) => {
                    const parts = value.split('|');
                    const label = parts[0] || '';
                    const description = parts[1] || '';
                    const date_uploaded = parts[2] || '';

                    updated[key] = { label, description, date_uploaded };
                    simplified[key] = label;
                    });

                    // save parsed details
                    allUpdated[val] = updated;
                    // replace charts value_labels with only label strings
                    charts[val].value_labels = simplified;
                }
            });

            setUploadsExtraData(allUpdated);
            setCharts(charts);
            setTaxonomy(buildTaxonomy(response));
            setLoaded(true);
        }).catch((error) => {
            setCharts({});
            setTaxonomy([[], {}]);
            setLoaded(true)
        });
    }

    function getChartIdsAtNodePath(nodePath, _taxonomy) {
        _taxonomy = _taxonomy || taxonomy;
        return nodePath.reduce(
            (curNode, nextNodeName) => nextNodeName ? curNode[1][nextNodeName] : curNode,
            _taxonomy)[0];
    }

    function _chartMatches(regex, chart_id) {
        const chart = charts[chart_id] || {};
        // Does the chart label match?
        if (regex.test(chart.label)) return true;

        // Do any chart value labels match?
        const labels = chart.value_labels || [];
        if (Object.values(labels).some((label) => regex.test(label))) return true;

        // no match
        return false;
    }

    function _filteredTaxonomy(regex, tree) {
        const filteredTaxonomy = {};

        Object.entries(tree).forEach(([name, node]) => {
            // If Folder Name matches, include entire folder
            if (regex.test(name)) {
                filteredTaxonomy[name] = node;
            } else { // Else check child Folders and Graphs
                // Check Graph Names and Value Labels
                const chart_ids = node[0] || [];
                const chartsMatched = chart_ids.filter(_chartMatches.bind(this, regex));

                // Check child Folders
                const filteredChildren = _filteredTaxonomy(regex, node[1]);
                const childNodesMatched = Object.keys(filteredChildren).length;

                // If either match, include this Folder and it's Filtered Children
                if (chartsMatched.length || childNodesMatched) {
                    filteredTaxonomy[name] = [chartsMatched, filteredChildren];
                }
            }
        });
        return filteredTaxonomy;
    }

    function filteredTaxonomy(regex, tree) {
        tree = tree || taxonomy[1];
        return [[], _filteredTaxonomy(regex, tree)];
    }

    useEffect(() => {
        if (appState.activeCompany && explorerModule) {
            setTaxonomy([[], {}]);
            setLoaded(false);
            loadChartsData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [explorerModule, appState.activeCompany])

    useEffect(() => {
        if (appState.activeCompany) {
          setTaxonomy([[], {}]);
          loadChartsData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appState.activeCompany])

    // eslint-disable-next-line
    const charts_store_output = {
        loaded: loaded,
        charts: charts,
        chartsRef: chartsRef,
        taxonomy: taxonomy,
        known_chart_ids: known_chart_ids,

        filterable,
        orderable,
        filterable2,
        sortable,
        h3FeatureMap,
        modelable,
        comparable,
        life_events: life_events,
        uploadsExtraData: uploadsExtraData,

        findPathToFirstLeafNode: findPathToFirstLeafNode,
        pathIsInTaxonomy: pathIsInTaxonomy,
        buildCharts: buildCharts,
        buildTaxonomy: buildTaxonomy,
        getChartIdsAtNodePath: getChartIdsAtNodePath,
        loadChartsData: loadChartsData,
        filteredTaxonomy: filteredTaxonomy
    };

    return charts_store_output;
}
